import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Mail, MessageCircle, Inbox, Clock, CheckCircle2, AlertCircle,
  Link2, Unlink, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import CommunicationsInbox from '../components/CommunicationsInbox';
import MyGmailConnection from '../components/MyGmailConnection';
import SmsInbox from '../components/SmsInbox';
import ScheduledEmailsList from '../components/ScheduledEmailsList';
import TeamBoard from '../components/TeamBoard';
import { GmailReconnectBanner } from '../components/GmailReconnectBanner';
import { useDesktopNotifications } from '../hooks/useRealtime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { label: 'Header redesign', status: 'done' },
  { label: 'Gmail OAuth + shared mailbox connection', status: 'done' },
  { label: 'Inbox UI & thread reader', status: 'done' },
  { label: 'Compose, reply, attachments', status: 'done' },
  { label: 'Per-user mailboxes', status: 'done' },
  { label: 'Shared inbox collaboration (assignment, notes, status)', status: 'done' },
  { label: 'Twilio SMS', status: 'done' },
  { label: 'Real-time push (Pub/Sub)', status: 'done' },
];

function PhaseRow({ label, status }) {
  const cfg = {
    done: { icon: <CheckCircle2 size={14} className="text-emerald-600" />, text: 'text-foreground' },
    in_progress: { icon: <Clock size={14} className="text-amber-600" />, text: 'text-foreground font-medium' },
    pending: { icon: <Clock size={14} className="text-muted-foreground" />, text: 'text-muted-foreground' },
  }[status];
  return (
    <li className={`flex items-center gap-2 text-sm ${cfg.text}`}>
      {cfg.icon}
      <span>{label}</span>
      {status === 'in_progress' && (
        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 ml-1">active</span>
      )}
    </li>
  );
}

function DesktopNotificationsBanner() {
  const { permission, request } = useDesktopNotifications();
  // Hide if already granted/denied OR if Notifications aren't supported
  if (typeof Notification === 'undefined') return null;
  if (permission === 'granted' || permission === 'denied') return null;
  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 flex items-center justify-between gap-3" data-testid="desktop-notif-banner">
      <p className="text-sm text-blue-900">
        Enable browser notifications to get instant alerts for new emails and texts — even when this tab is in the background.
      </p>
      <Button size="sm" onClick={request} data-testid="enable-desktop-notif-btn">
        Enable notifications
      </Button>
    </div>
  );
}

export default function Communications() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [personalStatus, setPersonalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [sharedRes, personalRes] = await Promise.all([
        axios.get(`${API}/communications/connection-status`).catch(e => ({ data: null, error: e })),
        axios.get(`${API}/communications/personal/connection-status`).catch(e => ({ data: null, error: e })),
      ]);
      setStatus(sharedRes.data);
      setPersonalStatus(personalRes.data);
    } catch (e) {
      console.error('Failed to fetch communications status:', e);
      toast.error('Could not load connection status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // OAuth callback feedback (the backend redirects here with ?gmail_connected=1|0)
  useEffect(() => {
    const connected = searchParams.get('gmail_connected');
    if (connected === '1') {
      const email = searchParams.get('email') || 'mailbox';
      const which = searchParams.get('mailbox') || 'shared';
      toast.success(`Connected ${which === 'personal' ? 'your personal' : 'shared'} mailbox: ${email}`);
      fetchStatus();
      // Clean URL
      searchParams.delete('gmail_connected');
      searchParams.delete('email');
      searchParams.delete('mailbox');
      setSearchParams(searchParams, { replace: true });
    } else if (connected === '0') {
      const err = searchParams.get('error') || 'unknown_error';
      toast.error(`Connection failed: ${err.replace(/_/g, ' ')}`);
      searchParams.delete('gmail_connected');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus]);

  const handleConnect = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please sign in again before connecting Gmail');
      return;
    }
    // Top-level navigation — Authorization header can't be set, so we pass
    // the token as a query param (the backend validates it the same way).
    window.location.href = `${API}/oauth/gmail/start?token=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect the shared mailbox? You can reconnect at any time. ' +
      'Note: this only removes the connection from this app — it does not revoke access in Google. ' +
      'To fully revoke, visit https://myaccount.google.com/permissions.'
    )) return;
    setActing(true);
    try {
      await axios.post(`${API}/communications/disconnect`);
      toast.success('Shared mailbox disconnected');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setActing(false);
    }
  };

  const connectedAtDisplay = status?.connected_at
    ? new Date(status.connected_at).toLocaleString()
    : null;

  const reconnectBanner = (
    <GmailReconnectBanner
      sharedEmail={status?.gmail_status === 'reconnect_required' ? status.email : null}
      personalEmail={personalStatus?.gmail_status === 'reconnect_required' ? personalStatus.email : null}
      isAdmin={isAdmin}
    />
  );
  const isConnected = !!status?.connected;
  const isPersonalConnected = !!personalStatus?.connected;
  const anyConnected = isConnected || isPersonalConnected;
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('email');

  // Build the mailbox list for the inbox switcher.
  const mailboxes = [];
  if (isConnected) mailboxes.push({ id: 'shared', label: `Shared · ${status.email}`, email: status.email });
  if (isPersonalConnected) mailboxes.push({ id: 'personal', label: `Me · ${personalStatus.email}`, email: personalStatus.email });

  // When connected, default to inbox view; show the connection card as a
  // smaller summary that can be expanded.
  if (anyConnected && !showSettings) {
    return (
      <div className="space-y-4" data-testid="communications-page">
        <DesktopNotificationsBanner />
        {reconnectBanner}
        <TeamBoard />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Communications</h1>
            <p className="text-sm text-muted-foreground mt-1">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 size={14} />
                Connected
              </span>
              {isConnected && (<><span className="mx-2">·</span><span className="font-mono">Shared: {status.email}</span></>)}
              {isPersonalConnected && (<><span className="mx-2">·</span><span className="font-mono">Me: {personalStatus.email}</span></>)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(true)}
              data-testid="open-settings-btn"
            >
              Settings
            </Button>
          </div>
        </div>

        {/* Channel tabs */}
        <div className="flex items-center gap-1 border-b" data-testid="comms-channel-tabs">
          <button
            onClick={() => setActiveTab('email')}
            data-testid="comms-tab-email"
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="inline-flex items-center gap-1.5"><Mail size={14} /> Email</span>
          </button>
          <button
            onClick={() => setActiveTab('sms')}
            data-testid="comms-tab-sms"
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'sms'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="inline-flex items-center gap-1.5"><MessageCircle size={14} /> SMS</span>
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            data-testid="comms-tab-scheduled"
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'scheduled'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> Scheduled</span>
          </button>
        </div>

        {activeTab === 'email' && (
          <CommunicationsInbox
            onDisconnect={fetchStatus}
            senderEmail={status?.email || personalStatus?.email || ''}
            mailboxes={mailboxes}
          />
        )}
        {activeTab === 'sms' && <SmsInbox />}
        {activeTab === 'scheduled' && <ScheduledEmailsList />}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="communications-page">
      {reconnectBanner}
      <TeamBoard />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Communications</h1>
          <p className="text-muted-foreground mt-2">
            {isConnected
              ? `Connected to ${status.email}`
              : 'Unified inbox for email and SMS — building in phases.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(false)}
              data-testid="back-to-inbox-btn"
            >
              ← Back to Inbox
            </Button>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
            <Clock size={14} />
            IN DEVELOPMENT
          </div>
        </div>
      </div>

      {/* Shared Mailbox Connection */}
      <Card className="p-6" data-testid="shared-mailbox-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              status?.connected ? 'bg-emerald-100' : 'bg-muted'
            }`}>
              <Mail size={22} className={status?.connected ? 'text-emerald-600' : 'text-muted-foreground'} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Shared Mailbox</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Connect your team's Gmail account (e.g. <span className="font-mono text-foreground">admin@elitehomerehab.com</span>) so any authorized admin can read and send mail from it inside the app.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchStatus} disabled={loading} data-testid="refresh-status-btn">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        <div className="mt-5 p-4 rounded-lg border bg-muted/30">
          {loading ? (
            <p className="text-sm text-muted-foreground">Checking connection…</p>
          ) : status?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 size={18} />
                <span className="font-semibold">Connected</span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground min-w-24">Email</dt>
                  <dd className="font-mono">{status.email}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground min-w-24">Connected</dt>
                  <dd>{connectedAtDisplay || '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-muted-foreground min-w-24">Refresh token</dt>
                  <dd>{status.has_refresh_token ? (
                    <span className="text-emerald-700">✓ Stored (auto-renew enabled)</span>
                  ) : (
                    <span className="text-amber-700">⚠ Missing — reconnect to enable auto-renew</span>
                  )}</dd>
                </div>
              </dl>
              {isAdmin && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleConnect}
                    disabled={acting}
                    data-testid="reconnect-shared-mailbox-btn"
                  >
                    <Link2 size={14} className="mr-2" />
                    Reconnect / Switch account
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisconnect}
                    disabled={acting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    data-testid="disconnect-shared-mailbox-btn"
                  >
                    <Unlink size={14} className="mr-2" />
                    Disconnect
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle size={18} />
                <span className="font-medium">Not connected</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {isAdmin
                  ? 'Click below to authorize this app to access your shared Gmail mailbox. You will be redirected to Google to sign in as the shared account (e.g. admin@elitehomerehab.com) and approve the requested permissions.'
                  : 'Ask an administrator to connect the shared mailbox before you can use Communications.'}
              </p>
              {isAdmin && (
                <Button
                  onClick={handleConnect}
                  disabled={acting}
                  data-testid="connect-shared-mailbox-btn"
                >
                  <Link2 size={16} className="mr-2" />
                  Connect Shared Mailbox
                </Button>
              )}
            </div>
          )}
        </div>

        {status?.connected && (
          <p className="text-xs text-muted-foreground mt-4">
            The inbox UI is coming in the next phase — once connected we'll surface threads, replies, attachments, and shared-inbox collaboration features here.
          </p>
        )}
      </Card>

      {/* Personal Mailbox Connection */}
      <MyGmailConnection />

      {/* Roadmap */}
      <Card className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <MessageSquare size={18} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Build progress</h2>
            <p className="text-sm text-muted-foreground">A single home for every conversation.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg border bg-white">
            <Mail size={20} className="text-primary mb-2" />
            <p className="text-sm font-semibold">Shared Inbox</p>
            <p className="text-xs text-muted-foreground mt-1">
              One OAuth connection to admin@ — every authorized staff member can read, reply, and assign threads.
            </p>
          </div>
          <div className="p-4 rounded-lg border bg-white">
            <Inbox size={20} className="text-primary mb-2" />
            <p className="text-sm font-semibold">Personal Mailbox</p>
            <p className="text-xs text-muted-foreground mt-1">
              Connect your own @elitehomerehab.com account to send and reply from your own identity.
            </p>
          </div>
          <div className="p-4 rounded-lg border bg-white">
            <MessageCircle size={20} className="text-primary mb-2" />
            <p className="text-sm font-semibold">Team SMS</p>
            <p className="text-xs text-muted-foreground mt-1">
              Two-way texting via your existing or a new Twilio number, threaded by contact.
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {PHASES.map((p, i) => (
            <PhaseRow key={i} label={`Phase ${i + 1} — ${p.label}`} status={p.status} />
          ))}
        </ul>
      </Card>
    </div>
  );
}
